/*
 *
 * A wrapper around the stock CatalogTable component to provide
 * arguably better defaults
 *
 */

import { TableColumn } from '@backstage/core-components'
import { useEntityList } from '@backstage/plugin-catalog-react'
import { useMemo } from 'react'
import {
  CatalogTable,
  CatalogTableProps,
  CatalogTableRow
} from '@backstage/plugin-catalog'
import { Entity } from '@backstage/catalog-model'

import { createComboColumn } from './comboColumn'
import { FetchedEntityRefLinks } from '../EntityRefLink/FetchedEntityRefLinks'
import {
  readPatchAppliedCount,
  readPatchStatus
} from '../catalog/catalogPatchAnnotations'
import { PatchStatusIndicator } from '../catalog/PatchStatusIndicator'

// Shim extraction to avoid touching component implementation
// too much. In case, it has to be updated from upstream code.
const columnFactories = CatalogTable.columns

function createTagsColumn(): TableColumn<CatalogTableRow> {
  return {
    ...columnFactories.createTagsColumn(),
    hidden: true,
    searchable: true
  }
}

function getRelationTitle(entity: Entity) {
  return entity.metadata.title || entity.metadata.name
}

function createSystemColumn(): TableColumn<CatalogTableRow> {
  return {
    title: 'System',
    field: 'resolved.partOfSystemRelationTitle',
    width: 'auto',
    cellStyle: {
      maxWidth: '25%'
    },
    render: ({ resolved }) => (
      <FetchedEntityRefLinks
        entityRefs={resolved.partOfSystemRelations}
        defaultKind="system"
        getTitle={getRelationTitle}
      />
    )
  }
}

function createOwnerColumn(): TableColumn<CatalogTableRow> {
  return {
    title: 'Owner',
    field: 'resolved.ownedByRelationsTitle',
    width: 'auto',
    cellStyle: {
      maxWidth: '20%'
    },
    render: ({ resolved }) => (
      <FetchedEntityRefLinks
        entityRefs={resolved.ownedByRelations}
        defaultKind="group"
        getTitle={getRelationTitle}
      />
    )
  }
}
function createKindColumn(): TableColumn<CatalogTableRow> {
  return {
    title: 'Kind',
    field: 'entity.kind',
    width: 'auto',
    cellStyle: {
      maxWidth: '15%',
      whiteSpace: 'nowrap'
    },
    render: ({ entity }: { entity: Entity }) => entity.kind
  }
}

/*
 * The two `CatalogPatch` columns are what turns
 * `/catalog?filters[kind]=catalogpatch` into the progress board that replaces
 * the spreadsheet. Both read annotations whose keys contain dots and a slash,
 * so neither can use material-table's `field` path traversal.
 */

function createPatchStatusColumn(): TableColumn<CatalogTableRow> {
  return {
    title: 'Patch status',
    width: 'auto',
    cellStyle: {
      maxWidth: '15%',
      whiteSpace: 'nowrap'
    },
    customSort({ entity: entity1 }, { entity: entity2 }) {
      // Unreported patches sort last: they are the ones the status pass has not
      // caught up with, not a state anybody is looking for.
      return (readPatchStatus(entity1) ?? '~').localeCompare(
        readPatchStatus(entity2) ?? '~'
      )
    },
    customFilterAndSearch(query, { entity }) {
      return (readPatchStatus(entity) ?? '').includes(query.toLocaleLowerCase('en-US'))
    },
    render: ({ entity }) => <PatchStatusIndicator status={readPatchStatus(entity)} />
  }
}

function createPatchAppliedCountColumn(): TableColumn<CatalogTableRow> {
  function countOf(entity: Entity): number {
    return readPatchAppliedCount(entity) ?? -1
  }

  return {
    title: 'Entities corrected',
    width: 'auto',
    type: 'numeric',
    cellStyle: {
      maxWidth: '10%'
    },
    customSort({ entity: entity1 }, { entity: entity2 }) {
      return countOf(entity1) - countOf(entity2)
    },
    // Only a `"*"` patch reports a count — an exact one resolves its targets
    // instead, and its per-target verdicts live on its own page.
    render: ({ entity }) => readPatchAppliedCount(entity) ?? '—'
  }
}

function createSpecTypeColumn(): TableColumn<CatalogTableRow> {
  return {
    ...columnFactories.createSpecTypeColumn(),
    cellStyle: {
      minWidth: '10em',
      maxWidth: '15em'
    }
  }
}

export function CustomCatalogTable(props: CatalogTableProps) {
  const { columns, actions, tableOptions = {}, emptyContent } = props

  const { entities, filters } = useEntityList()

  const defaultColumns: TableColumn<CatalogTableRow>[] = useMemo(() => {
    return [
      columnFactories.createTitleColumn({ hidden: true }),
      createComboColumn({
        defaultKind: filters.kind?.value
      }),
      ...createEntitySpecificColumns(),
      // FORK: description is already in the combo column
      //columnFactories.createMetadataDescriptionColumn()
      createTagsColumn()
    ]

    function createEntitySpecificColumns(): TableColumn<CatalogTableRow>[] {
      const baseColumns = [
        createSystemColumn(),
        createOwnerColumn(),
        createSpecTypeColumn()
        // FORK: we don’t include the lifecycle column in the overview
        //columnFactories.createSpecLifecycleColumn(),
      ]
      switch (filters.kind?.value) {
        case 'user':
          return []
        case 'domain':
        case 'system':
          return [createOwnerColumn()]
        case 'group':
        case 'template':
          return [createSpecTypeColumn()]
        case 'location':
          return [createSpecTypeColumn(), columnFactories.createSpecTargetsColumn()]
        case 'catalogpatch':
          return [createPatchStatusColumn(), createPatchAppliedCountColumn()]
        case 'entity':
          return [
            createKindColumn(),
            createSystemColumn(),
            createOwnerColumn(),
            createSpecTypeColumn()
          ]
        default:
          return baseColumns
      }
    }
  }, [filters.kind?.value])

  // Magic ahead! Backstage relies on “material-table” (https://github.com/mbrn/material-table)
  // component under the hood. The component has many performance problems and one of them
  // leads to behavior when filtering a 1000-row table down to a few rows (less than 20 which
  // is a page size) completelly freezes browser for 4-5 seconds. The bottleneck is somewhat
  // unclear, but profiler shows the problem is with re-creating, re-mounting, re-forwarding,
  // re-rendering of styled MTableRows React component.
  // A simple kludge to completely drop the existing table and render a new one when entities
  // change is enough to work around the issue.
  const hackyKey = entities.length.toString()

  return (
    <CatalogTable
      key={hackyKey}
      columns={columns ?? defaultColumns}
      actions={actions}
      tableOptions={tableOptions}
      emptyContent={emptyContent}
    />
  )
}
